import type {
  AdminAttentionCounts,
  AdminPaymentRequestDTO,
  AdminUserListItem,
  ApiResponse,
  AuthResponse,
  AutosaveProjectInput,
  ChannelDTO,
  CreateChannelInput,
  CreateEmojiPresetInput,
  CreateCustomCategoryInput,
  CreateProjectInput,
  CustomCategoryDTO,
  EditPackTitleInput,
  EditPackTitleResult,
  EmojiPresetDTO,
  ListProjectsQuery,
  ListUsersQuery,
  MembershipState,
  Paginated,
  PaymentStatus,
  Permission,
  PremiumPlanDTO,
  PremiumStatusDTO,
  ProjectDTO,
  ProjectRevisionDTO,
  ProjectRevisionSummaryDTO,
  PublishJobDTO,
  PublishProjectInput,
  Role,
  SetPremiumInput,
  SetRoleInput,
  StickerKind,
  UpdateChannelInput,
  UpdateEmojiPresetInput,
  UpdateProfileInput,
  UpdateProjectInput,
  UsageSummary,
  UserDTO,
} from '@astral/shared';

/** Channel-membership status for the signed-in user. */
export interface MembershipStatusDTO {
  state: MembershipState;
  /** The channel to link to when the user is not a member. */
  channel: string;
  /** False when server-side enforcement is switched off. */
  enforced: boolean;
}

/** Result of checking a proposed sticker-pack short name. */
export interface PackNameCheck {
  valid: boolean;
  error?: string;
  suffix: string;
  finalName?: string;
  url?: string;
  available: boolean;
}

/**
 * The processed-asset payload returned by the media pipeline.
 *
 * Media now lives in the backend media store: `media` is the reference the
 * project persists, and `src` is a SHORT-LIVED signed URL for rendering only —
 * it is never saved (the server blanks it on write and re-mints it on read).
 */
export interface ProcessedSticker {
  assetId: string;
  media: { kind: 'asset'; assetId: string };
  kind: StickerKind;
  src: string;
  format: string;
  size: number;
  width: number;
  height: number;
  durationMs?: number;
}

// When VITE_BACKEND_URL is set (e.g. production), requests go to that absolute
// host. When empty (the default), requests are same-origin `/api/...` — in dev
// the Vite server proxies them to the backend, which keeps the Mini App and API
// on one HTTPS origin behind a single tunnel (no mixed-content, no CORS).
const BASE_URL = (import.meta.env.VITE_BACKEND_URL ?? '').replace(/\/$/, '');
const TOKEN_STORAGE_KEY = 'astral.token';

/** An error thrown when the API returns a non-ok envelope or the request fails. */
export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

let token: string | null = (() => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
})();

export function setToken(value: string | null): void {
  token = value;
  try {
    if (value) localStorage.setItem(TOKEN_STORAGE_KEY, value);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* storage unavailable — keep the in-memory token */
  }
}

export function getToken(): string | null {
  return token;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | number | undefined>,
): Promise<T> {
  // If BASE_URL is absolute it wins; if empty, resolve against the current
  // origin so requests are same-origin (dev proxy / single tunnel).
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiClientError('NETWORK', 'Network error', 0);
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError('INTERNAL_ERROR', 'Malformed server response', response.status);
  }

  if (!payload.ok) {
    throw new ApiClientError(
      payload.error.code,
      payload.error.message,
      response.status,
      payload.error.details,
    );
  }
  return payload.data;
}

export interface UploadOptions {
  /**
   * Stable per-item id that survives this item's retries. The backend replays
   * the recorded answer for an id it has already completed, so a retry after a
   * lost response returns the ORIGINAL asset instead of minting a second one.
   */
  uploadId?: string;
  /** Caller-owned cancellation (component unmount, user cancel). */
  signal?: AbortSignal;
  /** Per-attempt deadline. Without one a stalled request hangs forever. */
  timeoutMs?: number;
}

/**
 * How long a single upload attempt may run.
 *
 * Generous, because the request covers the whole round trip — a 25 MB photo
 * over a weak mobile link, then a queued server-side encode. It exists to bound
 * a request that has genuinely stalled (a suspended WebView, a half-open
 * socket), not to police slow ones: a timeout is classified as RETRYABLE, so
 * cutting a live request short only costs a retry.
 */
export const UPLOAD_TIMEOUT_MS = 90_000;

/**
 * Upload a file via multipart/form-data (the browser sets the boundary).
 *
 * Every failure leaves as a typed {@link ApiClientError} whose `code`/`status`
 * the shared classifier can turn into a failure reason — including the two the
 * old implementation could not express at all: a request that timed out, and
 * one that was deliberately abandoned.
 */
async function upload<T>(path: string, file: File, options: UploadOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  const form = new FormData();
  /*
   * ORDER MATTERS. The backend reads this field from the multipart parser's
   * "fields seen so far" at the moment it takes the file part, so it must be
   * written BEFORE the file — a trailing field would not be parsed yet and the
   * upload would silently lose its idempotency.
   */
  if (options.uploadId) form.append('uploadId', options.uploadId);
  form.append('file', file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  /*
   * The caller's signal and this attempt's deadline are combined into one
   * controller, so whichever fires first aborts the fetch — and the two are
   * still told apart afterwards, because a deliberate cancel must not be
   * retried while a timeout must.
   */
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? UPLOAD_TIMEOUT_MS);
  const onExternalAbort = (): void => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);

  try {
    let response: Response;
    try {
      response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: form,
        signal: controller.signal,
      });
    } catch {
      if (timedOut) throw new ApiClientError('TIMEOUT', 'Upload timed out', 0);
      if (options.signal?.aborted) throw new ApiClientError('CANCELLED', 'Upload cancelled', 0);
      throw new ApiClientError('NETWORK', 'Network error', 0);
    }

    let payload: ApiResponse<T>;
    try {
      payload = (await response.json()) as ApiResponse<T>;
    } catch {
      /*
       * A non-JSON body from a 5xx is a proxy or a crash, not a malformed
       * answer from this API — it must keep its status so it classifies as
       * retryable rather than becoming an opaque internal error.
       */
      throw new ApiClientError(
        response.ok ? 'INTERNAL_ERROR' : 'UPSTREAM_UNAVAILABLE',
        'Malformed server response',
        response.status,
      );
    }
    if (!payload.ok) {
      throw new ApiClientError(payload.error.code, payload.error.message, response.status, payload.error.details);
    }
    return payload.data;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/** Typed client for every backend endpoint. */
export const api = {
  // Auth
  loginWithTelegram: (initData: string) =>
    request<AuthResponse>('POST', '/api/auth/telegram', { initData }),
  loginDev: () => request<AuthResponse>('POST', '/api/auth/dev'),
  me: () => request<{ user: UserDTO; permissions: Permission[] }>('GET', '/api/me'),
  updateMe: (input: UpdateProfileInput) =>
    request<{ user: UserDTO; permissions: Permission[] }>('PATCH', '/api/me', input),

  // Projects
  listProjects: (query: Partial<ListProjectsQuery> = {}) =>
    request<Paginated<ProjectDTO>>('GET', '/api/projects', undefined, {
      search: query.search,
      status: query.status,
      category: query.category,
      tag: query.tag,
      limit: query.limit,
      offset: query.offset,
    }),
  getProject: (id: string) => request<ProjectDTO>('GET', `/api/projects/${id}`),
  createProject: (input: CreateProjectInput) =>
    request<ProjectDTO>('POST', '/api/projects', input),
  updateProject: (id: string, input: UpdateProjectInput) =>
    request<ProjectDTO>('PATCH', `/api/projects/${id}`, input),
  deleteProject: (id: string) =>
    request<{ id: string; deleted: boolean }>('DELETE', `/api/projects/${id}`),
  duplicateProject: (id: string) =>
    request<ProjectDTO>('POST', `/api/projects/${id}/duplicate`),
  autosaveProject: (id: string, input: AutosaveProjectInput) =>
    request<ProjectDTO>('POST', `/api/projects/${id}/autosave`, input),
  /** History LIST — metadata only; snapshots are fetched one at a time. */
  listRevisions: (id: string) =>
    request<ProjectRevisionSummaryDTO[]>('GET', `/api/projects/${id}/revisions`),
  /** One revision's restorable snapshot, with asset URLs freshly signed. */
  getRevision: (id: string, revisionId: string) =>
    request<ProjectRevisionDTO>('GET', `/api/projects/${id}/revisions/${revisionId}`),
  checkPackName: (id: string, name: string) =>
    request<PackNameCheck>('GET', `/api/projects/${id}/pack-name`, undefined, { name }),
  publishProject: (id: string, input: PublishProjectInput = {}) =>
    request<{ jobId: string }>('POST', `/api/projects/${id}/publish`, input),
  publishJob: (jobId: string) => request<PublishJobDTO>('GET', `/api/publish-jobs/${jobId}`),
  syncProject: (id: string) =>
    request<{
      project: ProjectDTO;
      url: string;
      summary: { added: number; removed: number; emoji: number; reordered: number };
    }>('POST', `/api/projects/${id}/sync`),
  packStatus: (id: string) =>
    request<{ published: boolean; exists: boolean; count: number; title?: string | null }>(
      'GET',
      `/api/projects/${id}/pack-status`,
    ),
  /** Rename an already-published pack (base title and/or tag placement). */
  editPackTitle: (id: string, input: EditPackTitleInput) =>
    request<EditPackTitleResult>('POST', `/api/projects/${id}/pack-title`, input),

  // Custom project categories (user-owned)
  listCustomCategories: () => request<CustomCategoryDTO[]>('GET', '/api/custom-categories'),
  createCustomCategory: (input: CreateCustomCategoryInput) =>
    request<CustomCategoryDTO>('POST', '/api/custom-categories', input),
  deleteCustomCategory: (id: string) =>
    request<{ id: string; deleted: boolean }>('DELETE', `/api/custom-categories/${id}`),

  // Emoji presets (user-owned reusable ordered sequences)
  listEmojiPresets: () => request<EmojiPresetDTO[]>('GET', '/api/emoji-presets'),
  createEmojiPreset: (input: CreateEmojiPresetInput) =>
    request<EmojiPresetDTO>('POST', '/api/emoji-presets', input),
  updateEmojiPreset: (id: string, input: UpdateEmojiPresetInput) =>
    request<EmojiPresetDTO>('PATCH', `/api/emoji-presets/${id}`, input),
  deleteEmojiPreset: (id: string) =>
    request<{ id: string; deleted: boolean }>('DELETE', `/api/emoji-presets/${id}`),

  // Sticker media processing (backend converts to Telegram-ready output)
  processSticker: (file: File, options?: UploadOptions) =>
    upload<ProcessedSticker>('/api/stickers/process', file, options),

  // Required-channel membership (@AstralAtelier). The Telegram id is taken
  // from the verified session server-side; nothing is sent from here.
  membership: () => request<MembershipStatusDTO>('GET', '/api/me/membership'),
  /** A FRESH check that bypasses the cache — "I've joined, check again". */
  recheckMembership: () => request<MembershipStatusDTO>('POST', '/api/me/membership/recheck'),

  // Usage / plan
  usage: () => request<UsageSummary>('GET', '/api/usage'),

  // Channels
  listChannels: () => request<ChannelDTO[]>('GET', '/api/channels'),
  createChannel: (input: CreateChannelInput) => request<ChannelDTO>('POST', '/api/channels', input),
  updateChannel: (id: string, input: UpdateChannelInput) =>
    request<ChannelDTO>('PATCH', `/api/channels/${id}`, input),
  deleteChannel: (id: string) =>
    request<{ id: string; deleted: boolean }>('DELETE', `/api/channels/${id}`),

  // Premium (user-facing reads; purchase happens in the bot)
  premiumStatus: () => request<PremiumStatusDTO>('GET', '/api/premium/status'),
  premiumPlans: () => request<PremiumPlanDTO[]>('GET', '/api/premium/plans'),

  // Admin
  listUsers: (query: Partial<ListUsersQuery> = {}) =>
    request<Paginated<AdminUserListItem>>('GET', '/api/admin/users', undefined, {
      search: query.search,
      role: query.role,
      limit: query.limit,
      offset: query.offset,
    }),
  setUserRole: (input: SetRoleInput) => request<UserDTO>('POST', '/api/admin/users/role', input),
  setUserPremium: (input: SetPremiumInput) =>
    request<UserDTO>('POST', '/api/admin/users/premium', input),
  adminAttention: () => request<AdminAttentionCounts>('GET', '/api/admin/attention'),
  adminListPayments: (status?: PaymentStatus) =>
    request<{ items: AdminPaymentRequestDTO[]; total: number; limit: number; offset: number }>(
      'GET',
      '/api/admin/payments',
      undefined,
      { status },
    ),
  adminApprovePayment: (id: string) =>
    request<{ request: unknown; user: UserDTO }>('POST', `/api/admin/payments/${id}/approve`),
  adminRejectPayment: (id: string, reason?: string) =>
    request<{ request: unknown }>('POST', `/api/admin/payments/${id}/reject`, { reason }),
  /** Fetch a receipt image as an object URL (auth header can't ride on <img src>). */
  adminReceiptObjectUrl: async (id: string): Promise<string> => {
    const url = new URL(`${BASE_URL}/api/admin/payments/${id}/receipt`, window.location.origin);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) throw new ApiClientError('UNPROCESSABLE', 'Could not load receipt', res.status);
    return URL.createObjectURL(await res.blob());
  },
};

export type { Role };
